import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as poService from '@/services/poService';
import type {
  POFilter,
  CreatePORequest,
  UpdatePORequest,
  POStatus,
  POItemStatus,
  Vendor,
} from '@/types/purchaseorder';

// ============================================================================
// Query Keys
// ============================================================================

export const PO_QUERY_KEYS = {
  all: ['purchaseOrders'] as const,
  lists: () => [...PO_QUERY_KEYS.all, 'list'] as const,
  list: (filter?: POFilter) => [...PO_QUERY_KEYS.lists(), filter] as const,
  details: () => [...PO_QUERY_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...PO_QUERY_KEYS.details(), id] as const,
  vendors: () => ['vendors'] as const,
  nextNumber: () => ['po', 'nextNumber'] as const,
};

// ============================================================================
// Purchase Order Hooks
// ============================================================================

/**
 * Hook to fetch list of purchase orders with optional filtering
 */
export function usePurchaseOrders(filter?: POFilter) {
  return useQuery(
    PO_QUERY_KEYS.list(filter),
    () => poService.listPOs(filter),
    {
      keepPreviousData: true,
      staleTime: 30000,
      cacheTime: 300000,
    }
  );
}

/**
 * Hook to fetch a single purchase order by ID
 */
export function usePurchaseOrder(id: string | undefined) {
  return useQuery(
    PO_QUERY_KEYS.detail(id!),
    () => poService.getPO(id!),
    {
      enabled: !!id,
      staleTime: 30000,
    }
  );
}

/**
 * Hook to create a new purchase order
 */
export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation(
    (request: CreatePORequest) => poService.createPO(request),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PO_QUERY_KEYS.lists());
        queryClient.invalidateQueries(PO_QUERY_KEYS.nextNumber());
      },
    }
  );
}

/**
 * Hook to update an existing purchase order
 */
export function useUpdatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, request }: { id: string; request: UpdatePORequest }) => poService.updatePO(id, request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries(PO_QUERY_KEYS.lists());
        queryClient.setQueryData(PO_QUERY_KEYS.detail(variables.id), data);
      },
    }
  );
}

/**
 * Hook to update purchase order status
 */
export function useUpdatePOStatus() {
  const queryClient = useQueryClient();

  return useMutation(
    ({ id, status }: { id: string; status: POStatus }) => poService.updatePOStatus(id, status),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PO_QUERY_KEYS.lists());
        queryClient.invalidateQueries(PO_QUERY_KEYS.detail(variables.id));
      },
    }
  );
}

/**
 * Hook to update line item status
 */
export function useUpdateItemStatus() {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      poId,
      lineNo,
      status,
      receivedQty,
    }: {
      poId: string;
      lineNo: number;
      status: POItemStatus;
      receivedQty?: number;
    }) => poService.updateItemStatus(poId, lineNo, { status, received_qty: receivedQty }),
    {
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries(PO_QUERY_KEYS.lists());
        queryClient.invalidateQueries(PO_QUERY_KEYS.detail(variables.poId));
      },
    }
  );
}

/**
 * Hook to delete a purchase order
 */
export function useDeletePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation(
    (id: string) => poService.deletePO(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PO_QUERY_KEYS.lists());
      },
    }
  );
}

// ============================================================================
// Vendor Hooks
// ============================================================================

/**
 * Hook to fetch vendors for dropdown selection
 */
export function useVendors() {
  return useQuery<Vendor[]>(
    PO_QUERY_KEYS.vendors(),
    () => poService.getVendors(),
    {
      staleTime: 60000, // Cache for 1 minute
      cacheTime: 300000, // Keep in cache for 5 minutes
    }
  );
}

// ============================================================================
// Utility Hooks
// ============================================================================

/**
 * Hook to get the next PO number (preview only)
 */
export function useNextPONumber() {
  return useQuery(
    PO_QUERY_KEYS.nextNumber(),
    () => poService.getNextPONumber(),
    {
      staleTime: 5000, // Refresh frequently
    }
  );
}
